import { useCallback, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ComponentFormField } from 'bom/components';
import { ComponentType } from 'bom/enums';
import { BomPartHelper } from 'bom/helpers';
import { IComponent } from 'bom/types';
import { Form } from 'core/components';
import { FormProvider, useNotificationContext } from 'core/states';
import { useJobPartActions } from 'jobs/hooks';
import { IJobPart } from 'jobs/types';

import { RemoveComponentConfirmation } from './RemoveComponentConfirmation';
import { upsertComponentSchema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  jobPart: IJobPart;
  onSubmitSuccess: (updatedJobPart: IJobPart) => void;
}

interface IUpsertComponentData {
  component: IComponent | null;
}

export const UpsertComponent = ({ isOpen, onClose, jobPart, onSubmitSuccess }: IProps) => {
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const isEdit = useMemo(() => BomPartHelper.hasComponentAssigned(jobPart), [jobPart]);

  const { upsertJobPartComponent } = useJobPartActions({ isEdit });

  const title = useMemo(
    () => (isEdit ? t('titleEditComponentName') : t('titleAddComponentName')),
    [isEdit, t]
  );

  const defaultValues = useMemo<IUpsertComponentData>(() => {
    if (isEdit && jobPart.componentId && jobPart.componentName) {
      return {
        component: {
          id: jobPart.componentId,
          name: jobPart.componentName,
        },
      } as IUpsertComponentData;
    }

    return {
      component: null,
    };
  }, [isEdit, jobPart]);

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IUpsertComponentData>) => {
      onClose();
      clearNotification();
      reset?.();
    },
    [clearNotification, onClose]
  );

  const onSubmit = useCallback(
    (reset?: UseFormReset<IUpsertComponentData>) => async (data: IUpsertComponentData) => {
      if (!data.component) {
        return;
      }

      const { success, component } = await upsertJobPartComponent({
        bomPart: {
          id: jobPart.bomPartId,
          component: {
            name: data.component.name,
          },
        },
      });

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);

        onSubmitSuccess({ ...jobPart, componentId: component.id, componentName: component.name });
      }, 1000);
    },
    [upsertJobPartComponent, jobPart, onSubmitSuccess, handleOnClose]
  );

  const onRemoveClick = useCallback(() => {
    setIsRemoveConfirmOpen(true);
  }, []);

  const onRemoveSuccess = useCallback(() => {
    onClose();

    onSubmitSuccess({ ...jobPart, componentId: null, componentName: null });
  }, [onSubmitSuccess, jobPart, onClose]);

  return (
    <>
      <FormProvider
        schema={upsertComponentSchema}
        defaultValues={defaultValues}
        resetDefaultValues={isOpen}
      >
        <Form
          isOpen={isOpen}
          onClose={handleOnClose}
          onSubmit={onSubmit}
          onRemove={isEdit ? onRemoveClick : undefined}
          title={title}
          submitBtnLabel={
            isEdit ? t('buttonLabelEditComponentName') : t('buttonLabelAddComponentName')
          }
          cancelBtnLabel={t('buttonLabelCancel')}
          removeBtnLabel={t('buttonLabelRemoveComponentName')}
          allowRemove={isEdit}
        >
          {isOpen && (
            <ComponentFormField requestPayload={{ filters: { type: ComponentType.PART } }} />
          )}
        </Form>
      </FormProvider>

      <RemoveComponentConfirmation
        isOpen={isRemoveConfirmOpen}
        onClose={() => setIsRemoveConfirmOpen(false)}
        onSuccess={onRemoveSuccess}
        jobPart={jobPart}
      />
    </>
  );
};
